/**
 * Hook do log as-of da projeção × realizado (Task #221/#20 — consome
 * /api/v2/pld/projecao-log). Alimenta o track record (#26): qual era a NOSSA
 * projeção as-of pros dias que já fecharam.
 *
 * Mesmo padrão do usePldProjecao: cache module-level keyed por submercado
 * (o log não é por mês — devolve a série toda do submercado), TTL 5 min.
 * Fallback gracioso: erro → data null + flag (a UI só sobrepõe se houver dado).
 */
import { useEffect, useState } from "react";
import { getPldProjecaoLog } from "../api/client";
import type { PldProjecaoLogResponse } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 5 * 60 * 1000;

interface CacheEntry {
  expiresAt: number;
  promise: Promise<PldProjecaoLogResponse>;
}

const cache = new Map<string, CacheEntry>();

export function invalidatePldProjecaoLogCache(): void {
  cache.clear();
}

export interface UsePldProjecaoLogState {
  data: PldProjecaoLogResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export function usePldProjecaoLog(submercado: string): UsePldProjecaoLogState {
  const [state, setState] = useState<UsePldProjecaoLogState>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    const key = submercado;

    function load() {
      const now = Date.now();
      let entry = cache.get(key);
      if (!entry || entry.expiresAt < now) {
        entry = {
          expiresAt: now + REFETCH_MS,
          promise: getPldProjecaoLog(submercado),
        };
        cache.set(key, entry);
      }
      entry.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache.delete(key);
          setState({ data: null, isLoading: false, error: err });
        });
    }

    setState((s) => ({ ...s, isLoading: true }));
    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [submercado]);

  return state;
}
